import React, { useState, useEffect, useCallback } from "react";
import toast from "react-hot-toast";
import { useGlobalContext } from "context/globalContext";
import { IoMdClose } from "react-icons/io";
import { createOrUpdateCountriesAPI } from "Api/countriesAPI";
import PropTypes from "prop-types";

const CreateOrUpdateCountriesModal = ({
  isOpen,
  onClose,
  onSubmit,
  isEditMode,
}) => {
  const {
    countriesFormData,
    setCountriesFormData,
    initialCountriesFormState,
    setFetchCountries,
  } = useGlobalContext();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [isDataEdited, setIsDataEdited] = useState(false);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = "hidden";
      document.body.style.height = "100%";
    } else {
      document.body.style.overflow = "";
      document.body.style.height = "";
    }

    if (!isOpen) {
      setCountriesFormData(initialCountriesFormState);
      setIsDataEdited(false);
    }

    return () => {
      document.body.style.overflow = "";
      document.body.style.height = "";
    };
  }, [isOpen, initialCountriesFormState, setCountriesFormData]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    const updatedFormData = { ...countriesFormData, [name]: value };
    setCountriesFormData(updatedFormData);

    const isEdited =
      updatedFormData.name !== initialCountriesFormState?.name ||
      updatedFormData.code !== initialCountriesFormState?.code ||
      updatedFormData.iso !== initialCountriesFormState?.iso;

    setIsDataEdited(isEdited);

    if (name === "key") {
      setErrorMessage("");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!isFormValid()) return;
    setIsSubmitting(true);
    const token = localStorage.getItem("jwtToken");
    const response = await createOrUpdateCountriesAPI(
      countriesFormData,
      setIsSubmitting,
      token
    );
    if (response?.success) {
      const successMessage = countriesFormData?.id
        ? "Country updated successfully"
        : "Country created successfully";
      toast.success(successMessage);

      onSubmit(response.data);
      onClose();
      setCountriesFormData(initialCountriesFormState);
      setFetchCountries(true);
    } else {
      toast.error(
        response?.message ||
          "Error creating/updating Country. Please try again."
      );
    }
  };

  const handleCancel = () => {
    setCountriesFormData(initialCountriesFormState);
    onClose();
  };

  const isFormValid = useCallback(() => {
    return (
      countriesFormData?.name?.trim() &&
      countriesFormData?.code.trim() &&
      countriesFormData?.iso.trim()
    );
  }, [countriesFormData]);

  if (!isOpen) return null;

  const buttonClass =
    isSubmitting || !isFormValid() || !isDataEdited
      ? "cursor-not-allowed bg-gray-400"
      : "bg-green-600 hover:bg-green-500";

  const buttonText = isSubmitting
    ? "Submitting..."
    : isEditMode
    ? "Update"
    : "Add";

  return (
    <div className="bg-black fixed inset-0 z-50 flex h-screen items-center justify-center bg-opacity-50">
      <div className="relative z-50 mx-auto flex  w-4/5 flex-col items-center justify-start rounded-xl bg-white p-6 shadow-xl dark:!bg-gray-10 ">
        <button
                                        type="button"
                                        onClick={handleCancel}
                                         className="absolute top-6 right-6 text-gray-500 hover:text-gray-600 focus:outline-none dark:hover:text-gray-100 "
                                      >
                                        <IoMdClose size={24} />
                                      </button>

        <form onSubmit={handleSubmit} className="w-full">
          <div className="mt-4 flex justify-center gap-4">
            <div className="w-1/2">
              <label
                className="mb-2 block font-bold text-gray-700 dark:text-white"
                htmlFor="name"
              >
                <span className="text-red-600">*</span> Name:
              </label>
              <input
                type="text"
                id="name"
                 autoComplete="off"
                name="name"
                value={countriesFormData?.name}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              />
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
            <div className="w-1/2">
              <label
                className="mb-2 block font-bold text-gray-700 dark:text-white"
                htmlFor="code"
              >
                <span className="text-red-600">*</span> Code:
              </label>
              <input
                type="number"
                id="code"
                 autoComplete="off"
                name="code"
                min="0"
                value={countriesFormData?.code}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              />
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
          </div>
          <div className="mt-4 mb-4 flex justify-start gap-4">
            <div className="w-1/2">
              <label
                className="mb-2 block font-bold text-gray-700 dark:text-white"
                htmlFor="iso"
              >
                <span className="text-red-600">*</span> ISO:
              </label>
              <input
                type="text"
                id="iso"
                name="iso"
                 autoComplete="off"
                value={countriesFormData?.iso}
                onChange={handleChange}
                className="focus:border-transparent h-11 w-full rounded-md border border-gray-300 px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 dark:!bg-gray-10 dark:text-white"
                required
              />
              {errorMessage && (
                <p className="mt-2 text-sm text-red-500">{errorMessage}</p>
              )}
            </div>
          </div>
          <div className="mb-8 flex justify-end space-x-4">
            <button
              type="submit"
              className={`rounded-md py-2 px-4 text-white focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-50 ${buttonClass}`}
              disabled={isSubmitting || !isFormValid() || !isDataEdited}
            >
              {buttonText}
            </button>
            <button
              type="button"
              onClick={handleCancel}
              className="rounded-md bg-red-600 py-2 px-4 text-white hover:bg-red-400 focus:outline-none focus:ring-2 focus:ring-gray-500 focus:ring-opacity-50"
            >
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

CreateOrUpdateCountriesModal.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  isEditMode: PropTypes.bool.isRequired,
};

export default CreateOrUpdateCountriesModal;
